import { RazorTable, RazorTableRow } from "../../../components/razor-reuse";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { formatTime } from "../../../utils/Helper";
import { ALL_ICONS } from "../../../assets/icons/AllIcons";
import CreateThresholdModal from "./modal/CreateThresholdModal";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/type";
import { getRemittanceRules } from "../../../redux/featureMgt";
import DotActionDropFragment from "../../../components/fragment/actionIconFragment/DotActionDropFragment";
import EditThresholdModal from "./modal/EditThresholdModal";
import SuccessModal from "../../../components/fragment/modal/SuccessModal";
import { systemRoles } from "../../../utils/roles";
import PaginationFragment from "../../../components/fragment/pagination/PaginationFragment";

export interface thresholdProp {
  max_spend?: string;
  min_spend?: string;
  smartsend_remit: string;
  smartsend_without_value: number;
  id: string;
}

export default function ACHNowManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const { user } = useSelector((state: RootState) => state.auth); // for user permission
  const { loading, remittance_rules } = useSelector(
    (state: RootState) => state.feature_mgt
  );

  const [modal, setModal] = useState({
    create: false,
    update: false,
    successText: "",
  });

  const [editData, setEditData] = useState<thresholdProp>({
    max_spend: "",
    min_spend: "",
    smartsend_remit: "",
    smartsend_without_value: 0,
    id: "",
  });

  const headerList = [
    "Minimum Limit",
    "Maximum Limit",
    "Remittance thresholds",
    "Created By",
    "Date & Time",
    "Action",
  ];

  // redirect if user has no permission
  useEffect(() => {
    if (
      !user?.permissions?.includes(systemRoles[33]) &&
      user?.permissions?.length > 0
    ) {
      navigate(ALL_ROUTES.DASHBOARD_OVERVIEW);
    }
  }, [user?.permissions]);

  useEffect(() => {
    dispatch(getRemittanceRules({ page }) as any);
  }, [dispatch, page]);

  return (
    <>
      <DashboardLayoutMain
        //   loading={loading}
        title="Feature Management"
      >
        <section className="px-[2rem] space-y-4 mt-[15px] pb-[40px]">
          <div className="bg-white py-[16px] px-[30px] flex items-center mb-[28px]">
            <p className="font-semibold text-[18px] text-[#252C32]">
              ACHNow Management
            </p>
          </div>
          <div className="bg-pry w-full max-w-[300px] px-[17px] py-[21px] space-y-[21px]">
            <p className="text-white text-[16px] font-medium select-none">
              Remittance Threshold
            </p>
            <button
              onClick={() => {
                if (user?.permissions?.includes(systemRoles[31])) {
                  setModal((prev) => ({ ...prev, create: true }));
                }
              }}
              className="text-pry bg-white cursor-pointer h-[30px] px-[10px] font-medium text-[14px] rounded-[4px] inline-flex gap-x-[10px] items-center"
            >
              <Plus className="size-[18px]" /> Add a new threshold
            </button>
          </div>
          <section className="pt-[30px]">
            <RazorTable tableStrip={true} headerList={headerList}>
              {remittance_rules?.data?.map((chi: any) => {
                return (
                  <RazorTableRow
                    onRowClick={() => {
                      localStorage?.setItem("cors_key", chi?.id);
                      navigate(
                        ALL_ROUTES?.DASHBOARD_FEATURE_MANAGEMENT_DETAILS
                      );
                    }}
                    loading={loading}
                    key={chi?.id}
                    one={chi?.min_spend !== null ? `$${chi?.min_spend}` : "---"}
                    two={chi?.max_spend !== null ? `$${chi?.max_spend}` : "---"}
                    three={
                      chi?.smartsend_remit !== null
                        ? `$${chi?.smartsend_remit}`
                        : "---"
                    }
                    four={`${chi?.created_by?.first_name ?? "---"} ${
                      chi?.created_by?.last_name ?? "---"
                    }`}
                    five={formatTime(chi?.created_at)}
                    six={
                      <DotActionDropFragment
                        list={[
                          {
                            label: "View History",
                            onClick: (val: thresholdProp) => {
                              localStorage?.setItem("cors_key", val?.id);
                              navigate(
                                ALL_ROUTES?.DASHBOARD_FEATURE_MANAGEMENT_DETAILS
                              );
                            },
                          },
                          {
                            label: "Edit",
                            onClick: (val: thresholdProp) => {
                              setModal((prev) => ({ ...prev, update: true }));
                              setEditData({
                                id: val.id,
                                min_spend: String(val?.min_spend || ""),
                                max_spend: String(val?.max_spend || ""),
                                smartsend_remit: String(
                                  val?.smartsend_remit || ""
                                ),
                                smartsend_without_value:
                                  val.smartsend_without_value ?? 1,
                              });
                            },
                            hide: !user?.permissions?.includes(systemRoles[32]),
                          },
                        ]}
                        detail={chi}
                      />
                    }
                  />
                );
              })}
            </RazorTable>
            {(remittance_rules?.data?.length ?? 0) < 1 && !loading && (
              <section className="size-full bg-white h-[300px] grid place-content-center">
                <figure className="size-fit mx-auto space-y-[8px]">
                  {ALL_ICONS.receipt_warning}
                </figure>
                <p className="text-[12px] text-gray-02">
                  No table records found
                </p>
              </section>
            )}
            <PaginationFragment
              currentPage={remittance_rules?.meta?.current_page || 1}
              perPage={remittance_rules?.meta?.per_page}
              totalPage={remittance_rules?.meta?.total}
              onPageChange={(num) => {
                setPage(num);
              }}
              hidePagination={loading}
            />
          </section>
        </section>
      </DashboardLayoutMain>
      <CreateThresholdModal
        visible={modal.create}
        onClose={() => {
          setModal((prev) => ({ ...prev, create: false }));
        }}
        onComplete={() => {
          setModal((prev) => ({
            ...prev,
            create: false,
            successText: "Threshold created successfully",
          }));
        }}
      />
      <EditThresholdModal
        visible={modal.update}
        data={editData}
        onClose={() => {
          setModal((prev) => ({ ...prev, update: false }));
          setEditData({
            max_spend: "",
            min_spend: "",
            smartsend_remit: "",
            smartsend_without_value: 0,
            id: "",
          });
        }}
        onComplete={() => {
          setEditData({
            max_spend: "",
            min_spend: "",
            smartsend_remit: "",
            smartsend_without_value: 0,
            id: "",
          });
          setModal((prev) => ({
            ...prev,
            update: false,
            successText: "Threshold updated successfully",
          }));
        }}
      />
      <SuccessModal
        title={modal.successText}
        btnText="Ok"
        visible={!!modal.successText}
        onConfirm={() => {
          setModal({ create: false, update: false, successText: "" });
        }}
        onClose={() => {
          setModal({ create: false, update: false, successText: "" });
        }}
      />
    </>
  );
}
